import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Checkbox from 'flarum/common/components/Checkbox';
import Switch from 'flarum/common/components/Switch';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Stream from 'flarum/common/utils/Stream';
import { section, tip } from './Field';
import { EVENTS, CHANNELS, TOURNAMENT_EVENTS } from '../utils/contactRules';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.notifications_tab.' + key, params);
const c = (key, params) => app.translator.trans('ppk2-profile.forum.contacts.' + key, params);

/**
 * Tab «Оповещения»: ONE table in the look of core «Настройки» (NotificationGrid classes):
 *  - Flarum's notification types (whatever the enabled extensions register in
 *    NotificationGrid.notificationTypes): «На сайте» / «Email» = the user's real Flarum
 *    preferences notify_<type>_alert / notify_<type>_email (the same as in «Настройки»),
 *    «Telegram» = ours, stored in ppk2Notifications["flarum:<type>"].telegram;
 *  - our events (ppk2Notifications[event].site / email / telegram).
 * «Сбросить по умолчанию» and the subscription switches below; everything is saved with
 * «Сохранить» and rolled back with «Отменить все изменения» (like the other tabs).
 */

const PPK2_ICONS = {
  messages: 'fas fa-comments',
  new_tournaments: 'fas fa-trophy',
  tournament_reminder: 'far fa-clock',
  host_open: 'fas fa-gamepad',
  portal_news: 'far fa-newspaper'
};

/** Column -> [Flarum method | null, our channel, icon] */
const COLUMNS = [['alert', 'site', 'fas fa-bell'], ['email', 'email', 'far fa-envelope'], [null, 'telegram', 'fab fa-telegram']];

/** Subscription switches (flarum/subscriptions), shown only when the preference exists. */
const SWITCHES = [['followAfterReply', 'flarum-subscriptions.forum.settings.follow_after_reply_label'], ['followAfterCreate', 'flarum-subscriptions.forum.settings.follow_after_create_label'], ['flarum-subscriptions.notify_for_all_posts', 'flarum-subscriptions.forum.settings.notify_for_all_posts_label']];

/** Flarum's notification types, read once from core NotificationGrid (lazy chunk of «Настройки»). */
const grid = {
  types: null,
  error: false,
  loading: false
};
export function loadGrid(user) {
  if (grid.types || grid.loading) return;
  grid.loading = true;
  flarum.reg.asyncModuleImport('flarum/forum/components/NotificationGrid').then(mod => {
    const G = mod.default || mod;
    const fake = Object.create(G.prototype);
    fake.attrs = {
      user
    };
    grid.types = G.prototype.notificationTypes.call(fake).toArray();
  }).catch(() => grid.error = true).finally(() => {
    grid.loading = false;
    m.redraw();
  });
}
const key = (type, method) => 'notify_' + type + '_' + method;
export function notifState(user) {
  const prefs = user.preferences() || {};
  const stored = user.attribute('ppk2Notifications') || {};
  const s = {
    initial: {
      prefs: {},
      tg: {},
      ppk2: {},
      switches: {}
    },
    prefs: {},
    tg: {},
    ppk2: {},
    switches: {}
  };

  // every notify_* preference Flarum knows for this user (registered types × methods)
  Object.keys(prefs).filter(k => k.indexOf('notify_') === 0).forEach(k => {
    s.initial.prefs[k] = !!prefs[k];
    s.prefs[k] = Stream(!!prefs[k]);
  });

  // «Telegram» of Flarum types: created on first use (types come from the lazy grid)
  Object.keys(stored).filter(e => e.indexOf('flarum:') === 0).forEach(e => s.initial.tg[e.substr(7)] = !!stored[e].telegram);
  s.tgOf = type => {
    if (!s.tg[type]) s.tg[type] = Stream(!!s.initial.tg[type]);
    return s.tg[type];
  };
  Object.keys(EVENTS).forEach(event => {
    s.initial.ppk2[event] = {};
    s.ppk2[event] = {};
    CHANNELS.forEach((ch, i) => {
      const v = stored[event] && typeof stored[event][ch] === 'boolean' ? stored[event][ch] : EVENTS[event][i];
      s.initial.ppk2[event][ch] = v;
      s.ppk2[event][ch] = Stream(v);
    });
  });
  SWITCHES.forEach(([k]) => {
    if (!(k in prefs)) return;
    s.initial.switches[k] = !!prefs[k];
    s.switches[k] = Stream(!!prefs[k]);
  });
  s.defaults = user.attribute('ppk2NotifyDefaults') || {};
  return s;
}

/** { preferences?, ppk2Notifications? } with changed entries only. */
export function notifPayload(s) {
  const preferences = {};
  Object.keys(s.prefs).forEach(k => {
    if (s.prefs[k]() !== s.initial.prefs[k]) preferences[k] = s.prefs[k]();
  });
  Object.keys(s.switches).forEach(k => {
    if (s.switches[k]() !== s.initial.switches[k]) preferences[k] = s.switches[k]();
  });
  const notif = {};
  Object.keys(s.tg).forEach(type => {
    if (s.tg[type]() !== !!s.initial.tg[type]) notif['flarum:' + type] = {
      telegram: s.tg[type]()
    };
  });
  Object.keys(EVENTS).forEach(event => {
    CHANNELS.forEach(ch => {
      if (s.ppk2[event][ch]() !== s.initial.ppk2[event][ch]) {
        notif[event] = notif[event] || {};
        notif[event][ch] = s.ppk2[event][ch]();
      }
    });
  });
  const data = {};
  if (Object.keys(preferences).length) data.preferences = preferences;
  if (Object.keys(notif).length) data.ppk2Notifications = notif;
  return data;
}

/** «Сбросить по умолчанию»: every checkbox of the table (Flarum + ours) back to its default. */
export function resetNotif(s) {
  Object.keys(s.prefs).forEach(k => k in s.defaults && s.prefs[k](!!s.defaults[k]));
  (grid.types || []).forEach(type => s.tgOf(type.name)(false));
  Object.keys(s.tg).forEach(type => s.tg[type](false));
  Object.keys(EVENTS).forEach(event => CHANNELS.forEach((ch, i) => s.ppk2[event][ch](EVENTS[event][i])));
}
export default class NotificationsTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    loadGrid(this.attrs.user);
  }

  /** The cells of one row: [Stream | null] per column. */
  flarumCells(s, type) {
    return COLUMNS.map(([method]) => method ? s.prefs[key(type, method)] || null : s.tgOf(type));
  }
  ppk2Cells(s, event) {
    return COLUMNS.map(([, ch]) => s.ppk2[event][ch]);
  }

  /** Same as core: a click on a row / column title toggles all of it, based on the first box. */
  toggleAll(streams) {
    const list = streams.filter(Boolean);
    if (!list.length || this.attrs.loading) return;
    const on = !list[0]();
    list.forEach(x => x(on));
  }
  row(label, icon, cells, extraClass) {
    return m("tr", {
      className: extraClass || ''
    }, m("td", {
      className: "NotificationGrid-groupToggle",
      onclick: () => this.toggleAll(cells)
    }, m(Icon, {
      name: icon
    }), " ", label), cells.map((stream, i) => m("td", {
      className: "NotificationGrid-checkbox"
    }, m(Checkbox, {
      state: stream ? stream() : false,
      disabled: !stream || this.attrs.loading,
      onchange: v => stream && stream(v)
    }, m("span", {
      className: "sr-only"
    }, app.translator.trans('core.forum.settings.notification_checkbox_a11y_label_template', {
      description: label,
      method: this.columnLabel(i)
    }))))));
  }
  columnLabel(i) {
    return c('notifications.' + COLUMNS[i][1]);
  }
  view() {
    const s = this.attrs.state;
    const loading = this.attrs.loading;
    const types = grid.types || [];
    const allCells = i => [...types.map(type => this.flarumCells(s, type.name)[i]), ...Object.keys(EVENTS).map(event => this.ppk2Cells(s, event)[i])];

    // Group «Турниры» first, then «Форум»: Flarum's own types + «Личные сообщения»
    const tournamentEvents = TOURNAMENT_EVENTS.filter(e => EVENTS[e]);
    const forumEvents = Object.keys(EVENTS).filter(e => !TOURNAMENT_EVENTS.includes(e));
    const ppk2Row = event => this.row(c('events.' + event), PPK2_ICONS[event] || 'fas fa-bell', this.ppk2Cells(s, event));
    const table = m("div", {
      className: "Ppk2NotifGridWrap"
    }, m("table", {
      className: "NotificationGrid Ppk2NotifGrid"
    }, m("thead", null, m("tr", null, m("td", {
      className: "Ppk2NotifGrid-corner"
    }, tip(t('table_tip'))), COLUMNS.map(([, ch, icon], i) => m("th", {
      className: "NotificationGrid-groupToggle",
      title: this.columnLabel(i),
      onclick: () => this.toggleAll(allCells(i))
    }, m(Icon, {
      name: icon
    }), " ", m("span", {
      className: "Ppk2NotifGrid-colText"
    }, this.columnLabel(i)))))), m("tbody", null, m("tr", {
      className: "Ppk2NotifGrid-group"
    }, m("td", {
      colspan: "4"
    }, t('group_tournaments'))), tournamentEvents.map(ppk2Row), m("tr", {
      className: "Ppk2NotifGrid-group"
    }, m("td", {
      colspan: "4"
    }, t('group_forum'))), grid.types ? types.map(type => this.row(type.label, type.icon, this.flarumCells(s, type.name))) : m("tr", null, m("td", {
      colspan: "4"
    }, grid.error ? m("span", {
      className: "Ppk2Muted"
    }, t('load_error')) : m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }))), forumEvents.map(ppk2Row))));
    const switches = SWITCHES.filter(([k]) => s.switches[k]).map(([k, label]) => m("div", {
      className: "Ppk2NotifSwitch"
    }, m(Switch, {
      state: s.switches[k](),
      disabled: loading,
      onchange: v => s.switches[k](v)
    }, app.translator.trans(label))));
    return m("div", {
      className: "Ppk2Tab Ppk2Tab--notifications"
    }, section(t('section_table')), table, m("div", {
      className: "Ppk2Note Ppk2NotifNote"
    }, m(Icon, {
      name: "fab fa-telegram"
    }), " ", c('notifications.tip')), m("div", {
      className: "Ppk2NotifReset"
    }, m(Button, {
      type: "button",
      className: "Button",
      icon: "fas fa-rotate-left",
      title: t('reset_tip'),
      disabled: loading,
      onclick: () => {
        resetNotif(s);
        app.alerts.show({
          type: 'info'
        }, t('reset_done'));
      }
    }, t('reset'))), switches.length ? [section(t('section_subscriptions')), m("div", {
      className: "Ppk2NotifSwitches"
    }, switches)] : null);
  }
}
flarum.reg.add('ppk2-profile', 'forum/components/NotificationsTab', NotificationsTab);